const MetricsProviderType = 'metrics';
const LoggingProviderType = 'logging';

class FilterChainBuilder {
    filters = [];
    _family;
    constructor() {
        this.filters = [];
    }
    static builder() {
        return new FilterChainBuilder();
    }
    clean() {
        return new FilterChainBuilder();
    }
    addFilter(filter) {
        if (isFilter(filter)) {
            this.filters.push(filter);
        }
        return this;
    }
    addFilters(filters) {
        if (filters) {
            this.filters = this.filters.concat(this.sanitizeFilters(filters));
        }
        return this;
    }
    withFilters(filters) {
        this.filters = this.sanitizeFilters(filters);
        return this;
    }
    sanitizeFilters(filters) {
        return filters
            .filter(entry => isFilter(entry))
            .map(entry => entry);
    }
    family(family) {
        this._family = family;
        return this;
    }
    build() {
        //Inverse order
        let filters = this.filters.sort((f1, f2) => f2.order() - f1.order());
        if (this._family) {
            filters = filters.filter(filter => filter.family() === this._family);
        }
        let chain = new FilterChain(new EndOfChain());
        for (const filter of filters) {
            chain = new FilterChain(filter, chain);
        }
        return chain;
    }
}
class Filter {
    config;
    init(filterConfig) {
        this.config = filterConfig;
    }
    family() {
        if (this.config && this.config.family) {
            return this.config.family;
        }
        return "orphan";
    }
    order() {
        if (this.config && (this.config.order || this.config.order === 0)) {
            return this.config.order;
        }
        return Number.MAX_SAFE_INTEGER;
    }
}
class FilterChain {
    filter;
    chain;
    constructor(filter, chain) {
        this.filter = filter;
        this.chain = chain;
    }
    async doFilter(request, response) {
        return this.filter.doFilter(request, response, this.chain);
    }
}
class EndOfChain extends Filter {
    /* istanbul ignore next */
    name() {
        return "End";
    }
    async doFilter() {
        //Do nothing. It's the end of the chain
        return Promise.resolve();
    }
}
//@ts-ignore this function aims to verify is whatever object is a filter, for that reason we have to accept any
function isFilter(object) {
    if (!object) {
        return false;
    }
    if (!('init' in object || 'order' in object || 'family' in object || 'doFilter' in object)) {
        return false;
    }
    return typeof object.init === 'function' &&
        typeof object.order === 'function' &&
        typeof object.family === 'function' &&
        typeof object.doFilter === 'function';
}

class Registry {
    registry = {};
    firstEntryDate;
    isDelayed(exportIntervalInMs) {
        return this.firstEntryDate !== undefined &&
            ((new Date()).getTime() - this.firstEntryDate.getTime()) >= exportIntervalInMs;
    }
    getMetricRegistry() {
        return this.registry;
    }
}

class Metric {
    name;
    key;
    labels;
    options;
    value;
    constructor(value) {
        this.value = value;
    }
    create(name, labels) {
        if (!name) {
            return this;
        }
        let metricKey = name;
        if (labels) {
            const orderedLabels = Object.keys(labels).sort((a, b) => a.localeCompare(b));
            for (const label of orderedLabels) {
                metricKey = `${metricKey}_${label}=${labels[label]}`;
            }
            this.labels = labels;
        }
        //TODO Reduce the size of the key
        this.key = btoa(metricKey);
        this.name = name;
        return this;
    }
    record(value) {
        this.value.record(value);
        return this;
    }
    merge(metric) {
        if (metric.constructor != this.constructor) {
            console.debug(`Metric [${metric.getName()}] can't be merged because it has a different type.`);
            return this;
        }
        if (metric.getIdentifier() != this.getIdentifier()) {
            console.debug(`Metric [${metric.getName()}] can't be merged because identifiers don't match.`);
            return this;
        }
        this.value.merge(metric.getValue());
        this.mergeOptions(metric.getOptions());
        return this;
    }
    mergeOptions(options) {
        if (options === undefined) {
            return;
        }
        if (!this.options) {
            this.options = options;
            return;
        }
        if (this.options.description === undefined && options.description !== undefined) {
            this.options.description = options.description;
        }
        if (this.options.system === undefined && options.system !== undefined) {
            this.options.system = options.system;
        }
        if (this.options.unit === undefined && options.unit !== undefined) {
            this.options.unit = options.unit;
        }
        //Note: Histogram boundaries cannot be merged because it affects the boundaries
    }
    getValue() {
        return this.value;
    }
    setOptions(options) {
        this.options = options;
        return this;
    }
    getOptions() {
        return this.options;
    }
    getIdentifier() {
        return this.key;
    }
    getName() {
        return this.name;
    }
    /* istanbul ignore next */
    getLabels() {
        return this.labels;
    }
}

class ObservabilityService {
    version;
    exporters;
    config;
    registry;
    publisherScheduler;
    constructor() {
        document.onvisibilitychange = () => {
            if (document.visibilityState === 'hidden') {
                this.forceExport();
            }
        };
        this.version = '0.4.0'; //pkg.version;
    }
    static getInstance() {
        if (!window.$$O11Y) {
            window.$$O11Y = new ObservabilityService();
        }
        return window.$$O11Y;
    }
    addExporter(exporter) {
        if (!this.exporters) {
            this.exporters = [];
        }
        this.exporters?.push(exporter);
        this.providerFactory();
    }
    recordCounter(metricName, amount, labels, options) {
        this.getRegistry().record('counter', metricName, amount, labels, this.appendSystemInfoToOptions(options));
        this.forceExportIfDelayed();
    }
    recordGauge(metricName, amount, labels, options) {
        this.getRegistry().record('gauge', metricName, amount, labels, this.appendSystemInfoToOptions(options));
        this.forceExportIfDelayed();
    }
    recordHistogram(metricName, amount, labels, options) {
        this.getRegistry().record('histogram', metricName, amount, labels, this.appendSystemInfoToOptions(options));
        this.forceExportIfDelayed();
    }
    forceExport() {
        this.export();
    }
    forceExportIfDelayed() {
        if (this.getRegistry().isDelayed((this.config === undefined) ? 1 : this.config.exportIntervalMillis)) {
            this.export();
        }
        else if (this.config !== undefined && this.publisherScheduler === undefined) {
            this.publisherScheduler = setTimeout(() => this.forceExportIfDelayed(), this.config.exportIntervalMillis);
        }
    }
    export() {
        if (this.exporters === undefined || this.exporters.length == 0) {
            console.warn('ObservabilitySDK :: No exporters available :: Dropping registry');
            this.createRegistry();
            this.publisherScheduler = undefined;
            return;
        }
        for (const exporter of this.exporters) {
            try {
                exporter.publish(this.getRegistry());
            }
            catch (e) {
                console.error(`ObservabilitySDK :: Fail to export metric to exporter`, e);
            }
        }
        this.createRegistry();
        this.publisherScheduler = undefined;
    }
    getRegistry() {
        if (!this.registry) {
            this.createRegistry();
        }
        if (this.registry) {
            return this.registry;
        }
        throw new Error("ObservabilitySDK :: Registry Builder haven't been configured");
    }
    createRegistry() {
        if (this.config && this.config.registryBuilder) {
            this.registry = this.config.registryBuilder();
        }
    }
    appendSystemInfoToOptions(options) {
        return {
            ...options,
            ...{
                system: {
                    name: this.getSystemName(),
                    version: this.getVersion()
                }
            }
        };
    }
    getSystemName() {
        return this.getSystemInfo()?.name || 'com.superbet.web.default';
    }
    getVersion() {
        return this.getSystemInfo()?.version || this.getWebAppVersion();
    }
    getSystemInfo() {
        return this.config?.system;
    }
    getWebAppVersion() {
        return window.superbetVersion || 'v0.0.0';
    }
    setConfig(config) {
        this.config = config;
        this.providerFactory();
    }
    providerFactory() {
        if ((!this.exporters && !this.config) || !this.exporters) {
            return;
        }
    }
}
const ObservabilitySDK = ObservabilityService.getInstance();

function isArrayEquals(a, b) {
    if (a === b)
        return true;
    if (a == null || b == null)
        return false;
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; ++i) {
        if (a[i] !== b[i])
            return false;
    }
    return true;
}
function getBrowserName() {
    const browserNames = [
        { regex: /^(?!.*(Seamonkey)).*Firefox.*/, name: 'Firefox' },
        { regex: /^(?!.*(Chromium|Edg|OPR)).*Chrome.*/, name: 'Chrome' },
        { regex: /^(?!.*(Chrome|Chromium)).*Safari.*/, name: 'Safari' },
        { regex: /OPR|Opera/, name: 'Opera' },
        { regex: /Edg/, name: 'Edge' },
    ];
    const userAgent = window.navigator?.userAgent;
    for (let i = 0; i < browserNames.length; ++i) {
        if (browserNames[i].regex.test(userAgent)) {
            return browserNames[i].name;
        }
    }
    return 'Other';
}
function getOperatingSystemName() {
    const operatingSystemNames = [
        { regex: /Windows/, name: 'Windows' },
        { regex: /^(?!.*(iPhone|iPad)).*(Macintosh|Mac OS X).*/, name: 'Mac' },
        { regex: /^(?!.*(Android)).*Linux.*/, name: 'Linux' },
        { regex: /iPhone|iPad/, name: 'iPhone' },
        { regex: /Android/, name: 'Android' },
    ];
    const userAgent = window.navigator?.userAgent;
    for (let i = 0; i < operatingSystemNames.length; ++i) {
        if (operatingSystemNames[i].regex.test(userAgent)) {
            return operatingSystemNames[i].name;
        }
    }
    return 'Other';
}

class HistogramMetricValue {
    registeredAt = new Date();
    count = 0;
    sum = 0;
    min;
    max = 0;
    buckets = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    boundaries = [0, 5, 10, 25, 50, 75, 100, 250, 500, 750, 1000, 2500, 5000, 7500, 10000];
    record(value) {
        if (value <= 0) {
            console.debug(`Ignoring instrumentation of metric. Histograms doesn't support values equals or smaller than 0.`);
            return this;
        }
        this.increaseCounter();
        this.updateMax(value);
        this.updateMin(value);
        this.updateSum(value);
        this.updateBuckets(value);
        return this;
    }
    merge(metric) {
        if (metric.count === 0) {
            return this;
        }
        if (!isArrayEquals(this.boundaries, metric.boundaries)) {
            return this;
        }
        this.count = this.count + metric.count;
        this.sum = this.sum + metric.sum;
        this.updateMax(metric.max);
        if (metric.min) {
            this.updateMin(metric.min);
        }
        this.mergeBuckets(metric.buckets);
        return this;
    }
    increaseCounter() {
        this.count++;
    }
    updateMax(value) {
        if (value > this.max) {
            this.max = value;
        }
    }
    updateMin(value) {
        if (this.min === undefined) {
            this.min = value;
            return;
        }
        if (value < this.min) {
            this.min = value;
        }
    }
    updateSum(value) {
        this.sum = this.sum + value;
    }
    updateBuckets(value) {
        if (!this.boundaries) {
            return;
        }
        for (let index = 0; index <= this.boundaries.length; index++) {
            if (index == this.boundaries.length) {
                this.buckets[index]++;
                break;
            }
            if (this.boundaries[index] >= value) {
                this.buckets[index]++;
                break;
            }
        }
    }
    mergeBuckets(buckets) {
        for (let index = 0; index < this.buckets.length; index++) {
            if (buckets[index] > 0) {
                this.buckets[index] = this.buckets[index] + buckets[index];
            }
        }
    }
    isInitialized() {
        return this.count > 0;
    }
    getRegisteredAt() {
        return this.registeredAt;
    }
    getCount() {
        return this.count;
    }
    getSum() {
        return this.sum;
    }
    getMin() {
        return this.min;
    }
    getMax() {
        return this.max;
    }
    getBuckets() {
        return this.buckets;
    }
    getBoundaries() {
        return this.boundaries;
    }
    setBoundaries(boundaries) {
        if (!boundaries || boundaries.length === 0) {
            return;
        }
        this.boundaries = boundaries.sort((n1, n2) => n1 - n2);
        this.buckets = new Array(this.boundaries.length + 1).fill(0);
    }
}

class SimpleMetricValue {
    registeredAt = new Date();
    value;
    constructor(initialValue) {
        this.value = initialValue;
    }
    record(value) {
        if (!this.value) {
            this.value = 0;
        }
        this.value = this.value + value;
        return this;
    }
    merge(metric) {
        if (!this.isInitialized() && metric.isInitialized()) {
            return metric;
        }
        if (this.isInitialized() && !metric.isInitialized()) {
            return this;
        }
        this.record(metric.getValue());
        return this;
    }
    getRegisteredAt() {
        return this.registeredAt;
    }
    isInitialized() {
        return this.value !== undefined;
    }
    getValue() {
        return this.value;
    }
}

export { EndOfChain, Filter, FilterChain, FilterChainBuilder, HistogramMetricValue, LoggingProviderType, Metric, MetricsProviderType, ObservabilitySDK, ObservabilityService, Registry, SimpleMetricValue, getBrowserName, getOperatingSystemName, isArrayEquals };
//# sourceMappingURL=index.esm.js.map
